import { SHIP_STATS, type ShipType } from '../../shared/constants';
import {
  HEX_DIRECTIONS,
  type HexCoord,
  hexAdd,
  hexEqual,
  hexKey,
  hexToPixel,
  type PixelCoord,
} from '../../shared/hex';
import { computeCourse, predictDestination } from '../../shared/movement';
import type {
  GameState,
  PlayerId,
  Ship,
  SolarSystemMap,
} from '../../shared/types/domain';
import { deriveTrainingFirstBurnDirection } from '../game/training-guidance';
import { MOBILE_BREAKPOINT_PX } from '../ui-breakpoints';

export interface CoursePreviewPlanningState {
  selectedShipId: string | null;
  burns: Map<string, number | null>;
  overloads: Map<string, number | null>;
  landingShips?: Set<string>;
  weakGravityChoices: Map<string, Record<string, boolean>>;
  hoverHex: HexCoord | null;
}

export interface CourseMarkerView {
  position: PixelCoord;
  size: number;
  fillColor: string;
  strokeColor: string;
  lineWidth: number;
  shadowBlur: number;
  shadowColor: string | null;
  label: string | null;
  labelColor: string | null;
  recommended?: boolean;
  coachLabel?: string | null;
}

export interface CourseArrowView {
  from: PixelCoord;
  to: PixelCoord;
  headLeft: PixelCoord;
  headRight: PixelCoord;
  color: string;
  lineWidth: number;
}

export interface WeakGravityMarkerView {
  position: PixelCoord;
  fillColor: string;
  strokeColor: string;
  labelColor: string;
  strikeFrom: PixelCoord | null;
  strikeTo: PixelCoord | null;
}

export interface GhostShipView {
  position: PixelCoord;
  owner: PlayerId;
  shipType: ShipType;
  alpha: number;
  heading: number;
}

export interface FuelCostLabelView {
  position: PixelCoord;
  text: string;
  color: string;
}

export interface DriftSegment {
  points: PixelCoord[];
  color: string;
  alpha: number;
}

export interface CourseCrashMarkerView {
  position: PixelCoord;
}

export interface CoursePreviewView {
  shipId: string;
  linePoints: PixelCoord[];
  lineColor: string;
  lineWidth: number;
  lineDash: number[];
  takeoffSegment: { points: PixelCoord[] } | null;
  gravityArrows: CourseArrowView[];
  ghostShip: GhostShipView | null;
  crashMarker: CourseCrashMarkerView | null;
  burnMarkers: CourseMarkerView[];
  burnArrow: CourseArrowView | null;
  overloadMarkers: CourseMarkerView[];
  overloadArrow: CourseArrowView | null;
  weakGravityMarkers: WeakGravityMarkerView[];
  pendingGravityArrows: CourseArrowView[];
  fuelCostLabel: FuelCostLabelView | null;
  driftSegments: DriftSegment[];
}

const buildDirectionMarker = (
  position: PixelCoord,
  isActive: boolean,
  isHovered: boolean,
  baseSize: number,
  activeSize: number,
  hoverDelta: number,
  activeFill: string,
  hoverFill: string,
  idleFill: string,
  activeStroke: string,
  idleStroke: string,
  activeShadow: string,
  hoverShadow: string,
  activeBlur: number,
  hoverBlur: number,
): CourseMarkerView => {
  const size =
    (isActive ? activeSize : baseSize) + (isHovered ? hoverDelta : 0);

  return {
    position,
    size,
    fillColor: isActive ? activeFill : isHovered ? hoverFill : idleFill,
    strokeColor: isActive || isHovered ? activeStroke : idleStroke,
    lineWidth: isActive || isHovered ? 2 : 1.5,
    shadowBlur: isHovered ? hoverBlur : isActive ? activeBlur : 0,
    shadowColor:
      isHovered || isActive ? (isHovered ? hoverShadow : activeShadow) : null,
    label: null,
    labelColor: null,
  };
};

const buildGravityArrow = (
  hex: HexCoord,
  direction: number,
  hexSize: number,
  color = 'rgba(255, 214, 84, 0.82)',
): CourseArrowView => {
  const start = hexToPixel(hex, hexSize);
  const target = hexToPixel(hexAdd(hex, HEX_DIRECTIONS[direction]), hexSize);

  const angle = Math.atan2(target.y - start.y, target.x - start.x);
  const arrowLength = 7;

  const tip = {
    x: start.x + Math.cos(angle) * arrowLength,
    y: start.y + Math.sin(angle) * arrowLength,
  };

  const headLength = 4;

  return {
    from: start,
    to: tip,
    headLeft: {
      x: tip.x - headLength * Math.cos(angle - 0.5),
      y: tip.y - headLength * Math.sin(angle - 0.5),
    },
    headRight: {
      x: tip.x - headLength * Math.cos(angle + 0.5),
      y: tip.y - headLength * Math.sin(angle + 0.5),
    },
    color,
    lineWidth: 1.75,
  };
};

const buildWeakGravityMarker = (
  hex: HexCoord,
  ignored: boolean,
  hexSize: number,
): WeakGravityMarkerView => {
  const position = hexToPixel(hex, hexSize);

  return {
    position,
    fillColor: ignored
      ? 'rgba(190, 150, 255, 0.16)'
      : 'rgba(190, 150, 255, 0.48)',
    strokeColor: ignored
      ? 'rgba(190, 150, 255, 0.68)'
      : 'rgba(210, 180, 255, 0.92)',
    labelColor: ignored
      ? 'rgba(210, 180, 255, 0.58)'
      : 'rgba(236, 226, 255, 0.96)',
    strikeFrom: ignored ? { x: position.x - 6, y: position.y + 4 } : null,
    strikeTo: ignored ? { x: position.x + 6, y: position.y - 4 } : null,
  };
};

const buildDirectionArrow = (
  from: PixelCoord,
  to: PixelCoord,
  color: string,
  lineWidth: number,
  headLength: number,
  insetPx: number,
): CourseArrowView => {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  const tipDist = Math.max(0, dist - insetPx);
  const tip = {
    x: from.x + Math.cos(angle) * tipDist,
    y: from.y + Math.sin(angle) * tipDist,
  };

  return {
    from,
    to: tip,
    headLeft: {
      x: tip.x - headLength * Math.cos(angle - 0.5),
      y: tip.y - headLength * Math.sin(angle - 0.5),
    },
    headRight: {
      x: tip.x - headLength * Math.cos(angle + 0.5),
      y: tip.y - headLength * Math.sin(angle + 0.5),
    },
    color,
    lineWidth,
  };
};

const buildBurnMarkers = (
  ship: GameState['ships'][number],
  burn: number | null,
  hoverHex: HexCoord | null,
  predictedDestination: HexCoord,
  hexSize: number,
  recommendedBurnDirection: number | null,
): CourseMarkerView[] => {
  if (ship.fuel <= 0 || ship.damage.disabledTurns > 0) return [];

  const markers: CourseMarkerView[] = [];

  HEX_DIRECTIONS.forEach((_, direction) => {
    if (burn === direction) return;

    const targetHex = hexAdd(predictedDestination, HEX_DIRECTIONS[direction]);
    const target = hexToPixel(targetHex, hexSize);
    const isHovered = hoverHex !== null && hexEqual(hoverHex, targetHex);

    const marker = buildDirectionMarker(
      target,
      false,
      isHovered,
      16,
      20,
      3,
      'rgba(100, 220, 255, 0.88)',
      'rgba(100, 220, 255, 0.52)',
      'rgba(100, 220, 255, 0.28)',
      '#64dcff',
      'rgba(100, 220, 255, 0.42)',
      '#64dcff',
      'rgba(100, 220, 255, 0.9)',
      8,
      12,
    );

    const showLabel =
      typeof window === 'undefined' || window.innerWidth > MOBILE_BREAKPOINT_PX;
    marker.label = showLabel ? String(direction + 1) : null;
    marker.labelColor = isHovered
      ? 'rgba(0, 0, 0, 0.9)'
      : 'rgba(210, 246, 255, 0.9)';

    if (direction === recommendedBurnDirection) {
      marker.recommended = true;
      marker.coachLabel = 'TRY';
      marker.size = 20;
      marker.fillColor = 'rgba(255, 205, 70, 0.46)';
      marker.strokeColor = '#ffd45a';
      marker.lineWidth = 2.5;
      marker.shadowBlur = 16;
      marker.shadowColor = 'rgba(255, 212, 90, 0.95)';
      marker.label = showLabel ? String(direction + 1) : '★';
      marker.labelColor = '#fff6c7';
    }

    markers.push(marker);
  });

  return markers;
};

const computeGhostHeading = (
  ship: GameState['ships'][number],
  _burn: number | null,
  hexSize: number,
): number => {
  if (
    ship.lastBurnDirection !== undefined &&
    ship.lastBurnDirection >= 0 &&
    ship.lastBurnDirection < HEX_DIRECTIONS.length
  ) {
    const dir = HEX_DIRECTIONS[ship.lastBurnDirection];
    const from = hexToPixel(ship.position, hexSize);
    const to = hexToPixel(hexAdd(ship.position, dir), hexSize);
    return Math.atan2(to.y - from.y, to.x - from.x);
  }

  const { velocity } = ship;
  if (velocity.dq === 0 && velocity.dr === 0) return 0;

  const from = hexToPixel(ship.position, hexSize);
  const to = hexToPixel(hexAdd(ship.position, velocity), hexSize);
  return Math.atan2(to.y - from.y, to.x - from.x);
};

const buildBurnArrow = (
  ship: GameState['ships'][number],
  burn: number | null,
  predictedDestination: HexCoord,
  hexSize: number,
): CourseArrowView | null => {
  if (burn === null) return null;
  if (ship.fuel <= 0 || ship.damage.disabledTurns > 0) return null;

  const targetHex = hexAdd(predictedDestination, HEX_DIRECTIONS[burn]);
  const from = hexToPixel(predictedDestination, hexSize);
  const to = hexToPixel(targetHex, hexSize);
  return buildDirectionArrow(from, to, '#64dcff', 2.75, 8, hexSize * 0.35);
};

const buildOverloadMarkers = (
  ship: GameState['ships'][number],
  burn: number | null,
  overload: number | null,
  hoverHex: HexCoord | null,
  predictedDestination: HexCoord,
  hexSize: number,
): CourseMarkerView[] => {
  if (burn === null) return [];

  const stats = SHIP_STATS[ship.type];

  if (
    !stats?.canOverload ||
    ship.fuel < 2 ||
    ship.overloadUsed ||
    ship.damage.disabledTurns > 0
  ) {
    return [];
  }

  const burnDestination = hexAdd(predictedDestination, HEX_DIRECTIONS[burn]);

  const markers: CourseMarkerView[] = [];

  HEX_DIRECTIONS.forEach((_, direction) => {
    if (overload === direction) return;

    const targetHex = hexAdd(burnDestination, HEX_DIRECTIONS[direction]);
    const target = hexToPixel(targetHex, hexSize);

    markers.push(
      buildDirectionMarker(
        target,
        false,
        hoverHex !== null && hexEqual(hoverHex, targetHex),
        10,
        14,
        3,
        'rgba(255, 190, 86, 0.9)',
        'rgba(255, 190, 86, 0.52)',
        'rgba(255, 190, 86, 0.18)',
        '#ffbe56',
        'rgba(255, 190, 86, 0.38)',
        '#ffbe56',
        'rgba(255, 190, 86, 0.88)',
        4,
        8,
      ),
    );
  });

  return markers;
};

const buildOverloadArrow = (
  ship: GameState['ships'][number],
  burn: number | null,
  overload: number | null,
  predictedDestination: HexCoord,
  hexSize: number,
): CourseArrowView | null => {
  if (burn === null || overload === null) return null;

  const stats = SHIP_STATS[ship.type];
  if (
    !stats?.canOverload ||
    ship.fuel < 2 ||
    ship.overloadUsed ||
    ship.damage.disabledTurns > 0
  ) {
    return null;
  }

  const burnDestination = hexAdd(predictedDestination, HEX_DIRECTIONS[burn]);
  const overloadTarget = hexAdd(burnDestination, HEX_DIRECTIONS[overload]);
  const from = hexToPixel(burnDestination, hexSize);
  const to = hexToPixel(overloadTarget, hexSize);
  return buildDirectionArrow(from, to, '#ffbe56', 2.35, 7, hexSize * 0.32);
};

const DRIFT_ALPHAS = [0.34, 0.22];

const buildDriftSegments = (
  ship: Ship,
  course: ReturnType<typeof computeCourse>,
  map: SolarSystemMap,
  hexSize: number,
): DriftSegment[] => {
  if (course.outcome === 'crash' || course.outcome === 'landing') return [];

  const segments: DriftSegment[] = [];
  let pos = course.destination;
  let vel = course.newVelocity;
  let pending = course.enteredGravityEffects;

  for (let i = 0; i < DRIFT_ALPHAS.length; i++) {
    const synthetic: Ship = {
      ...ship,
      position: pos,
      velocity: vel,
      pendingGravityEffects: pending,
      lifecycle: 'active',
    };

    const drift = computeCourse(synthetic, null, map);

    const points = [pos, ...drift.path.slice(1)].map((hex) =>
      hexToPixel(hex, hexSize),
    );

    segments.push({
      points,
      color: drift.outcome === 'crash' ? '#ff5555' : '#64dcff',
      alpha: DRIFT_ALPHAS[i],
    });

    if (drift.outcome === 'crash' || drift.outcome === 'landing') break;

    pos = drift.destination;
    vel = drift.newVelocity;
    pending = drift.enteredGravityEffects;
  }

  return segments;
};

export const buildAstrogationCoursePreviewViews = (
  state: GameState,
  playerId: PlayerId,
  planning: CoursePreviewPlanningState,
  map: SolarSystemMap,
  hexSize: number,
  showTrainingRecommendation = false,
): CoursePreviewView[] => {
  if (state.phase !== 'astrogation' || state.activePlayer !== playerId) {
    return [];
  }

  const previews: CoursePreviewView[] = [];

  for (const ship of state.ships) {
    if (ship.owner !== playerId || ship.lifecycle === 'destroyed') {
      continue;
    }

    const burn = planning.burns.get(ship.id) ?? null;
    const isSelected = ship.id === planning.selectedShipId;
    const isDisabled = ship.damage.disabledTurns > 0;

    if (burn === null && !isSelected) continue;
    if (isDisabled && !isSelected) continue;

    const overload = planning.overloads.get(ship.id) ?? null;
    const weakGravityChoices = planning.weakGravityChoices.get(ship.id) ?? {};

    const course = computeCourse(ship, burn, map, {
      overload,
      weakGravityChoices,
      destroyedBases: state.destroyedBases,
      land: planning.landingShips?.has(ship.id),
    });

    const destination = hexToPixel(course.destination, hexSize);
    const predictedDestination = predictDestination(ship);
    const recommendedBurnDirection = showTrainingRecommendation
      ? deriveTrainingFirstBurnDirection(state, playerId, ship, map)
      : null;

    const takeoffSegment = null;
    const mainPath = course.path;

    previews.push({
      shipId: ship.id,
      linePoints: mainPath.map((hex) => hexToPixel(hex, hexSize)),
      lineColor: course.outcome === 'crash' ? '#ff5555' : '#64dcff',
      lineWidth: 2.35,
      lineDash: burn !== null ? [] : [6, 4],
      takeoffSegment,

      gravityArrows: course.gravityEffects
        .filter((gravity) => gravity.strength !== 'weak')
        .map((gravity) =>
          buildGravityArrow(gravity.hex, gravity.direction, hexSize),
        ),

      ghostShip:
        course.outcome === 'crash'
          ? null
          : {
              position: destination,
              owner: ship.owner,
              shipType: ship.type,
              alpha: 0.4,
              heading: computeGhostHeading(ship, burn, hexSize),
            },

      crashMarker:
        course.outcome === 'crash'
          ? { position: hexToPixel(course.crashHex, hexSize) }
          : null,

      burnMarkers:
        isSelected && !isDisabled
          ? buildBurnMarkers(
              ship,
              burn,
              planning.hoverHex,
              predictedDestination,
              hexSize,
              recommendedBurnDirection,
            )
          : [],

      burnArrow:
        isSelected && !isDisabled
          ? buildBurnArrow(ship, burn, predictedDestination, hexSize)
          : null,

      overloadMarkers:
        isSelected && !isDisabled
          ? buildOverloadMarkers(
              ship,
              burn,
              overload,
              planning.hoverHex,
              predictedDestination,
              hexSize,
            )
          : [],

      overloadArrow:
        isSelected && !isDisabled
          ? buildOverloadArrow(
              ship,
              burn,
              overload,
              predictedDestination,
              hexSize,
            )
          : null,

      weakGravityMarkers:
        isSelected && !isDisabled
          ? course.enteredGravityEffects
              .filter((gravity) => gravity.strength === 'weak')
              .map((gravity) =>
                buildWeakGravityMarker(
                  gravity.hex,
                  weakGravityChoices[hexKey(gravity.hex)] === true,
                  hexSize,
                ),
              )
          : [],

      pendingGravityArrows:
        isSelected && !isDisabled
          ? course.enteredGravityEffects
              .filter((gravity) => gravity.strength === 'full')
              .map((gravity) =>
                buildGravityArrow(
                  gravity.hex,
                  gravity.direction,
                  hexSize,
                  'rgba(120, 240, 240, 0.68)',
                ),
              )
          : [],

      fuelCostLabel: null,

      driftSegments: isSelected
        ? buildDriftSegments(ship, course, map, hexSize)
        : [],
    });
  }

  return previews;
};
